<div class="modal-header">
  <h4 class="modal-title">
    <fa-icon [icon]="faCreditCard" class="mr-2"></fa-icon>
    {{ getStepTitle() }}
  </h4>
  <button type="button" class="close" aria-label="Close" (click)="modal.dismiss('cancel')" [disabled]="processing">
    <span aria-hidden="true">&times;</span>
  </button>
</div>

<div class="modal-body">
  <!-- Progress indicator -->
  <div class="progress mb-4" style="height: 8px;">
    <div class="progress-bar"
         [style.width.%]="(step / maxStep) * 100"
         role="progressbar">
    </div>
  </div>

  <!-- Step 1: Payment Amount -->
  <div *ngIf="step === 1">
    <div class="form-group">
      <label for="paymentAmount" class="font-weight-bold">Amount to Pay</label>
      <div class="input-group input-group-lg">
        <div class="input-group-prepend">
          <span class="input-group-text">$</span>
        </div>
        <input type="text"
               class="form-control"
               id="paymentAmount"
               [(ngModel)]="paymentAmount"
               (input)="onPaymentAmountInput()"
               (blur)="formatPaymentAmount()"
               placeholder="0.00"
               pattern="[0-9]+(\.[0-9]{1,2})?"
               [class.is-invalid]="errors.amount">
      </div>
      <div *ngIf="errors.amount" class="invalid-feedback d-block">
        {{ errors.amount }}
      </div>
      <small class="form-text text-muted">Enter the amount you would like to pay</small>
    </div>
  </div>

  <!-- Step 2: Card Information -->
  <div *ngIf="step === 2">
    <div class="row">
      <div class="col-12">
        <div class="form-group">
          <label for="cardNumber" class="font-weight-bold">Card Number</label>
          <input type="text"
                 class="form-control"
                 id="cardNumber"
                 [(ngModel)]="cardData.cardNumber"
                 (input)="formatCardNumber()"
                 placeholder="1234 5678 9012 3456"
                 maxlength="19"
                 [class.is-invalid]="errors.cardNumber">
          <div *ngIf="errors.cardNumber" class="invalid-feedback">
            {{ errors.cardNumber }}
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-4">
        <div class="form-group">
          <label for="cardExpMonth" class="font-weight-bold">Month</label>
          <select class="form-control"
                  id="cardExpMonth"
                  [(ngModel)]="cardData.cardExpMonth"
                  [class.is-invalid]="errors.expiry">
            <option value="">Month</option>
            <option *ngFor="let month of months" [value]="month.value">
              {{ month.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="cardExpYear" class="font-weight-bold">Year</label>
          <select class="form-control"
                  id="cardExpYear"
                  [(ngModel)]="cardData.cardExpYear"
                  [class.is-invalid]="errors.expiry">
            <option value="">Year</option>
            <option *ngFor="let year of years" [value]="year">
              {{ year }}
            </option>
          </select>
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="cardCvv" class="font-weight-bold">CVV</label>
          <input type="text"
                 class="form-control"
                 id="cardCvv"
                 [(ngModel)]="cardData.cardCvv"
                 placeholder="123"
                 maxlength="4"
                 [class.is-invalid]="errors.cvv">
        </div>
      </div>
    </div>

    <div *ngIf="errors.expiry" class="text-danger small mb-2">
      {{ errors.expiry }}
    </div>
    <div *ngIf="errors.cvv" class="text-danger small mb-2">
      {{ errors.cvv }}
    </div>

    <div class="alert alert-info">
      <fa-icon [icon]="faLock" class="mr-2"></fa-icon>
      Your payment information is encrypted and securely tokenized. Card details never touch our servers.
    </div>
  </div>

  <!-- Step 3: Billing Information -->
  <div *ngIf="step === 3">
    <div class="row">
      <div class="col-md-6">
        <div class="form-group">
          <label for="firstName" class="font-weight-bold">First Name *</label>
          <input type="text"
                 class="form-control"
                 id="firstName"
                 [(ngModel)]="billingInfo.firstName"
                 [class.is-invalid]="errors.firstName">
          <div *ngIf="errors.firstName" class="invalid-feedback">
            {{ errors.firstName }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="lastName" class="font-weight-bold">Last Name *</label>
          <input type="text"
                 class="form-control"
                 id="lastName"
                 [(ngModel)]="billingInfo.lastName"
                 [class.is-invalid]="errors.lastName">
          <div *ngIf="errors.lastName" class="invalid-feedback">
            {{ errors.lastName }}
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <div class="form-group">
          <label for="email" class="font-weight-bold">Email *</label>
          <input type="email"
                 class="form-control"
                 id="email"
                 [(ngModel)]="billingInfo.email"
                 [class.is-invalid]="errors.email">
          <div *ngIf="errors.email" class="invalid-feedback">
            {{ errors.email }}
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="form-group">
          <label for="phone">Phone</label>
          <input type="tel"
                 class="form-control"
                 id="phone"
                 [(ngModel)]="billingInfo.phone">
        </div>
      </div>
    </div>

    <div class="form-group">
      <label for="address" class="font-weight-bold">Address *</label>
      <input type="text"
             class="form-control"
             id="address"
             [(ngModel)]="billingInfo.address"
             [class.is-invalid]="errors.address">
      <div *ngIf="errors.address" class="invalid-feedback">
        {{ errors.address }}
      </div>
    </div>

    <div class="row">
      <div class="col-md-4">
        <div class="form-group">
          <label for="city" class="font-weight-bold">City *</label>
          <input type="text"
                 class="form-control"
                 id="city"
                 [(ngModel)]="billingInfo.city"
                 [class.is-invalid]="errors.city">
          <div *ngIf="errors.city" class="invalid-feedback">
            {{ errors.city }}
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="state" class="font-weight-bold">State *</label>
          <input type="text"
                 class="form-control"
                 id="state"
                 [(ngModel)]="billingInfo.state"
                 [class.is-invalid]="errors.state">
          <div *ngIf="errors.state" class="invalid-feedback">
            {{ errors.state }}
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="form-group">
          <label for="zip" class="font-weight-bold">ZIP Code *</label>
          <input type="text"
                 class="form-control"
                 id="zip"
                 [(ngModel)]="billingInfo.zip"
                 [class.is-invalid]="errors.zip">
          <div *ngIf="errors.zip" class="invalid-feedback">
            {{ errors.zip }}
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Summary -->
    <div class="card bg-light mt-3">
      <div class="card-body">
        <h6 class="card-title">Payment Summary</h6>
        <div class="d-flex justify-content-between">
          <span>Amount:</span>
          <span class="font-weight-bold">${{ getFormattedPaymentAmount() }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span>Card:</span>
          <span>{{ payjunctionService.maskCardNumber(cardData.cardNumber) }}</span>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="modal-footer">
  <button type="button"
          class="btn btn-secondary"
          *ngIf="step > 1"
          (click)="previousStep()"
          [disabled]="processing">
    Previous
  </button>

  <button type="button"
          class="btn btn-light"
          (click)="modal.dismiss('cancel')"
          [disabled]="processing">
    Cancel
  </button>

  <button type="button"
          class="btn btn-primary"
          (click)="nextStep()"
          [disabled]="processing">
    <span *ngIf="processing" class="spinner-border spinner-border-sm mr-2"></span>
    <span *ngIf="step < maxStep">Next</span>
    <span *ngIf="step === maxStep">
      <fa-icon [icon]="faLock" class="mr-1"></fa-icon>
      Process Payment
    </span>
  </button>
</div>